import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

function fileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getURL(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  tagsText: '',
  category_id: '',
  is_published: false,
  published_at: '',
  sort_order: 0,
  cover_image: null,
  gallery: null,
  project_url: '',
};

function normalizeTags(text) {
  const raw = String(text || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  return raw;
}

function makeSlug(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function AdminSiteRealisationsPage() {
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [items, setItems] = React.useState([]);
  const [categories, setCategories] = React.useState([]);

  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const [slugTouched, setSlugTouched] = React.useState(false);

  const [coverDragOver, setCoverDragOver] = React.useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = React.useState('');
  const [removeExistingCover, setRemoveExistingCover] = React.useState(false);
  const coverInputRef = React.useRef(null);

  const [galleryDragOver, setGalleryDragOver] = React.useState(false);
  const [galleryPreviewUrls, setGalleryPreviewUrls] = React.useState([]);
  const [removedExistingGallery, setRemovedExistingGallery] = React.useState([]);
  const [existingGalleryOrder, setExistingGalleryOrder] = React.useState([]);
  const [existingGalleryDirty, setExistingGalleryDirty] = React.useState(false);
  const galleryInputRef = React.useRef(null);

  const galleryDragRef = React.useRef({ type: '', index: -1 });

  React.useEffect(() => {
    return () => {
      try { if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl); } catch { }
      try { galleryPreviewUrls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { } }); } catch { }
    };
  }, [coverPreviewUrl, galleryPreviewUrls]);

  const fetchCategories = async () => {
    try {
      const res = await pb.collection('site_realisations_categories').getList(1, 200, { sort: 'sort_order, name' });
      setCategories(res?.items ?? []);
    } catch {
      setCategories([]);
    }
  };

  const fetchAll = async () => {
    setError('');
    try {
      const res = await pb.collection('site_realisations').getList(1, 200, { sort: 'sort_order, -created', expand: 'category_id' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des réalisations');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchCategories();
    fetchAll();
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => clearTimeout(t);
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_realisations').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...items];
    await persistOrder(ordered);
    await fetchAll();
  };

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    setCoverDragOver(false);
    setGalleryDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
    setRemovedExistingGallery([]);
    setExistingGalleryOrder([]);
    setExistingGalleryDirty(false);
    setGalleryPreviewUrls([]);
    setForm({
      ...emptyForm,
      sort_order: items.length ? Math.max(...items.map(i => Number(i.sort_order || 0))) + 1 : 0,
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setSlugTouched(true);
    setCoverDragOver(false);
    setGalleryDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
    setRemovedExistingGallery([]);
    setExistingGalleryOrder(Array.isArray(it?.gallery) ? it.gallery : []);
    setExistingGalleryDirty(false);
    setGalleryPreviewUrls([]);
    const tags = Array.isArray(it?.tags) ? it.tags : (it?.tags ? it.tags : []);
    setForm({
      title: it?.title || '',
      slug: it?.slug || '',
      excerpt: it?.excerpt || '',
      content: it?.content || '',
      tagsText: Array.isArray(tags) ? tags.join(', ') : '',
      category_id: it?.category_id || '',
      is_published: !!it?.is_published,
      published_at: it?.published_at ? String(it.published_at).slice(0, 10) : '',
      sort_order: Number(it?.sort_order || 0),
      cover_image: null,
      gallery: null,
      project_url: it?.project_url || '',
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
    setCoverDragOver(false);
    setGalleryDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
    setRemovedExistingGallery([]);
    setExistingGalleryOrder([]);
    setExistingGalleryDirty(false);
    setGalleryPreviewUrls([]);
  };

  const handleTitleChange = (value) => {
    setForm((f) => {
      const next = { ...f, title: value };
      if (!slugTouched) next.slug = makeSlug(value);
      return next;
    });
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const setCoverFile = (file) => {
    if (!(file instanceof File)) return;
    setRemoveExistingCover(false);
    setForm((f) => ({ ...f, cover_image: file }));
    try { if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl); } catch { }
    try {
      setCoverPreviewUrl(URL.createObjectURL(file));
    } catch {
      setCoverPreviewUrl('');
    }
  };

  const setGalleryFiles = (files) => {
    const arr = Array.from(files || []).filter((f) => f instanceof File);
    setForm((f) => ({ ...f, gallery: arr.length ? arr : null }));
    try { galleryPreviewUrls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { } }); } catch { }
    try {
      const urls = arr.map((f) => {
        try { return URL.createObjectURL(f); } catch { return ''; }
      }).filter(Boolean);
      setGalleryPreviewUrls(urls);
    } catch {
      setGalleryPreviewUrls([]);
    }
  };

  const toggleRemoveExistingGallery = (filename) => {
    const name = String(filename || '');
    if (!name) return;
    setExistingGalleryDirty(true);
    setRemovedExistingGallery((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      return [...prev, name];
    });
  };

  const moveArrayItem = (arr, from, to) => {
    const a = Array.isArray(arr) ? [...arr] : [];
    if (from === to) return a;
    if (from < 0 || from >= a.length) return a;
    if (to < 0 || to >= a.length) return a;
    const [it] = a.splice(from, 1);
    a.splice(to, 0, it);
    return a;
  };

  const reorderNewGallery = (from, to) => {
    setForm((f) => {
      const nextGallery = moveArrayItem(Array.isArray(f.gallery) ? f.gallery : [], from, to);
      return { ...f, gallery: nextGallery.length ? nextGallery : null };
    });
    setGalleryPreviewUrls((prev) => moveArrayItem(prev, from, to));
  };

  const reorderExistingGallery = (from, to) => {
    setExistingGalleryDirty(true);
    setExistingGalleryOrder((prev) => moveArrayItem(prev, from, to));
  };

  const removeSelectedGalleryAt = (idx) => {
    setForm((f) => {
      const arr = Array.isArray(f.gallery) ? [...f.gallery] : [];
      if (idx < 0 || idx >= arr.length) return f;
      arr.splice(idx, 1);
      return { ...f, gallery: arr.length ? arr : null };
    });
    setGalleryPreviewUrls((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (idx < 0 || idx >= next.length) return prev;
      const url = next[idx];
      try { if (url) URL.revokeObjectURL(url); } catch { }
      next.splice(idx, 1);
      return next;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      data.append('title', form.title || '');
      data.append('slug', form.slug || '');
      data.append('excerpt', form.excerpt || '');
      data.append('content', form.content || '');
      data.append('sort_order', String(Number(form.sort_order || 0)));
      data.append('is_published', form.is_published ? 'true' : 'false');
      data.append('published_at', form.published_at || '');
      data.append('project_url', form.project_url || '');
      if (form.category_id) data.append('category_id', form.category_id);

      const tags = normalizeTags(form.tagsText);
      data.append('tags', JSON.stringify(tags));

      if (removeExistingCover) data.append('cover_image', '');
      if (form.cover_image instanceof File) data.append('cover_image', form.cover_image);

      const existingGallery = (editing?.id && Array.isArray(existingGalleryOrder)) ? existingGalleryOrder : [];
      const keptExistingGallery = existingGallery.filter((name) => !removedExistingGallery.includes(String(name)));

      if (editing?.id && (existingGalleryDirty || removedExistingGallery.length)) {
        if (!keptExistingGallery.length && !(form.gallery && form.gallery.length)) {
          data.append('gallery', '');
        } else {
          keptExistingGallery.forEach((name) => data.append('gallery', String(name)));
        }
      }

      if (form.gallery && form.gallery.length) {
        Array.from(form.gallery).forEach((f) => {
          if (f instanceof File) data.append('gallery', f);
        });
      }

      if (editing?.id) await pb.collection('site_realisations').update(editing.id, data);
      else await pb.collection('site_realisations').create(data);

      await fetchAll();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette réalisation ?')) return;
    try {
      await pb.collection('site_realisations').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleTogglePublished = async (it) => {
    try {
      const next = !(it?.is_published === true);
      await pb.collection('site_realisations').update(it.id, {
        is_published: next,
        published_at: next ? (it.published_at || new Date().toISOString()) : it.published_at,
      });
      await fetchAll();
    } catch (e) {
      alert(e?.message || "Erreur de mise à jour");
    }
  };

  const move = async (id, dir) => {
    const ordered = [...items].sort((a, b) => {
      const ao = Number(a.sort_order || 0);
      const bo = Number(b.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return (a.created || '').localeCompare(b.created || '');
    });
    const idx = ordered.findIndex(i => i.id === id);
    if (idx < 0) return;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;
    const a = ordered[idx];
    const b = ordered[swapWith];
    const ao = Number(a.sort_order || 0);
    const bo = Number(b.sort_order || 0);
    try {
      await Promise.all([
        pb.collection('site_realisations').update(a.id, { sort_order: bo }),
        pb.collection('site_realisations').update(b.id, { sort_order: ao }),
      ]);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Réalisations</h1>
          <p style={{ marginTop: 6 }}>Créez et publiez les réalisations (projets) affichées sur le site.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Nouvelle réalisation</span>
          </button>
        </div>
      </div>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 980px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="date"], .field input[type="number"], .field textarea, .field select{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 160px; resize: vertical; }
        .field input:focus, .field textarea:focus, .field select:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }

        .dropzone{
          border: 2px dashed var(--admin-border);
          border-radius: 12px;
          padding: 14px;
          background: var(--admin-panel-2);
          display:flex;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
          user-select:none;
        }
        .dropzone.dragover{
          border-color: rgba(79,70,229,0.55);
          box-shadow: 0 0 0 3px rgba(79,70,229,0.16);
        }
        .dropzone-left{ display:flex; flex-direction:column; gap:4px; }
        .dropzone-title{ font-weight:700; }
        .dropzone-sub{ color: var(--admin-muted); font-size:.9rem; }
        .cover-preview{ width: 140px; height: 92px; border-radius: 10px; object-fit: cover; border:1px solid var(--admin-border); background: rgba(0,0,0,0.06); }
        .cover-thumb{ position: relative; width: 140px; height: 92px; }
        .cover-thumb .cover-preview{ width: 140px; height: 92px; }
        .thumb-actions{ position:absolute; right: 6px; top: 6px; display:flex; gap: 6px; }
        .thumb-actions button{ cursor:pointer; border:1px solid var(--admin-border); background: rgba(0,0,0,0.45); color:#fff; padding: 6px; border-radius: 10px; }
        .thumb-actions button:hover{ background: rgba(0,0,0,0.60); }

        .thumbs{ display:flex; flex-wrap:wrap; gap: 8px; margin-top: 10px; }
        .thumb{ width: 96px; height: 64px; border-radius: 10px; object-fit: cover; border:1px solid var(--admin-border); background: rgba(0,0,0,0.06); }
        .thumb-wrap{ position: relative; width: 96px; height: 64px; }
        .thumb-wrap .thumb{ width: 96px; height: 64px; }
        .thumb-wrap.removed{ opacity: 0.35; }
        .thumb-wrap.draggable{ cursor: grab; }
        .thumb-wrap.draggable:active{ cursor: grabbing; }
        .thumb-wrap .thumb-actions{ position:absolute; right: 6px; top: 6px; display:flex; gap: 6px; }
        .thumb-wrap .thumb-actions button{ cursor:pointer; border:1px solid var(--admin-border); background: rgba(0,0,0,0.45); color:#fff; padding: 6px; border-radius: 10px; }
        .thumb-wrap .thumb-actions button:hover{ background: rgba(0,0,0,0.60); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="grid">
          {items.map((it, idx) => {
            const cover = fileUrl(it, 'cover_image', '600x0');
            const published = it?.is_published === true;
            const categoryName = it?.expand?.category_id?.name;
            return (
              <div
                key={it.id}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => {
                  e.preventDefault();
                  onDrop();
                }}
                title={`Glissez pour réordonner (position ${idx + 1})`}
                style={{ border: '1px solid var(--admin-border)', borderRadius: 12, overflow: 'hidden', background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))', cursor: 'grab' }}
              >
                <div style={{ height: 160, background: 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cover ? (
                    <img src={cover} alt={it.title || 'Réalisation'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ color: 'var(--admin-muted)' }}>Aucune image</div>
                  )}
                </div>

                <div style={{ padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: '1.02rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title || '—'}</div>
                      <div style={{ color: 'var(--admin-muted)', marginTop: 4, fontSize: '.9rem' }}>
                        slug: {it.slug || '—'}{categoryName ? ` · ${categoryName}` : ''}
                      </div>
                    </div>
                    <button
                      className={'toggle-pill ' + (published ? 'on' : 'off')}
                      onClick={() => handleTogglePublished(it)}
                      title={published ? 'Passer en brouillon' : 'Publier'}
                    >
                      {published ? 'Publié' : 'Brouillon'}
                    </button>
                  </div>

                  {it.excerpt && (
                    <div style={{ marginTop: 10, color: 'var(--admin-muted)', fontSize: '.9rem', lineHeight: 1.3, maxHeight: 60, overflow: 'hidden' }}>
                      {it.excerpt}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button className="header-pill" onClick={() => move(it.id, 'up')} title="Monter">⬆</button>
                    <button className="header-pill" onClick={() => move(it.id, 'down')} title="Descendre">⬇</button>
                    <span style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>Ordre: {idx + 1}</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button className="header-pill" title="Modifier" onClick={() => openEdit(it)}>✏️</button>
                      <button className="header-pill" title="Supprimer" onClick={() => handleDelete(it.id)}>🗑️</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {items.length === 0 && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <h3 style={{ margin: 0 }}>Aucune réalisation</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>Commencez par ajouter votre première réalisation.</p>
                <button className="btn-primary" onClick={openCreate} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                  border: '1px solid rgba(79,70,229,0.6)',
                  boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                  color: 'white'
                }}>
                  <span>Nouvelle réalisation</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Titre</label>
                <input type="text" value={form.title} onChange={(e) => handleTitleChange(e.target.value)} />
              </div>
              <div className="field">
                <label>Slug</label>
                <input type="text" value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="ex: mon-projet" />
              </div>
              <div className="field">
                <label>URL du projet (Live)</label>
                <input type="text" value={form.project_url} onChange={(e) => setForm(f => ({ ...f, project_url: e.target.value }))} placeholder="https://..." />
              </div>
              <div className="field">
                <label>Catégorie</label>
                <select value={form.category_id} onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">—</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="field full">
                <label>Extrait</label>
                <textarea value={form.excerpt} onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Contenu</label>
                <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Texte / HTML / contenu richtext" />
              </div>
              <div className="field">
                <label>Tags (séparés par virgules)</label>
                <input type="text" value={form.tagsText} onChange={(e) => setForm(f => ({ ...f, tagsText: e.target.value }))} placeholder="react, seo, ..." />
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field">
                <label>Publié</label>
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm(f => ({ ...f, is_published: e.target.checked }))} />
              </div>
              <div className="field">
                <label>Date de publication</label>
                <input type="date" value={form.published_at} onChange={(e) => setForm(f => ({ ...f, published_at: e.target.value }))} />
              </div>
              <div className="field">
                <label>Image de couverture</label>
                <div
                  className={`dropzone ${coverDragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setCoverDragOver(true); }}
                  onDragLeave={() => setCoverDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setCoverDragOver(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file) setCoverFile(file);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      coverInputRef.current?.click();
                    }
                  }}
                  onClick={() => coverInputRef.current?.click()}
                >
                  <div className="dropzone-left">
                    <div className="dropzone-title">Glisser-déposer une image</div>
                    <div className="dropzone-sub">ou cliquer pour choisir un fichier</div>
                    {removeExistingCover ? (
                      <div className="dropzone-sub" style={{ color: '#ef4444' }}>Image supprimée (en attente d’enregistrement)</div>
                    ) : null}
                  </div>
                  {(coverPreviewUrl || (editing?.id && fileUrl(editing, 'cover_image', '600x0'))) && !removeExistingCover ? (
                    <div className="cover-thumb" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <img
                        className="cover-preview"
                        src={coverPreviewUrl || fileUrl(editing, 'cover_image', '600x0')}
                        alt={coverPreviewUrl ? 'Aperçu' : 'Couverture actuelle'}
                      />
                      <div className="thumb-actions">
                        <button
                          type="button"
                          title="Supprimer l’image"
                          aria-label="Supprimer l’image"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setForm((f) => ({ ...f, cover_image: null }));
                            try { if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl); } catch { }
                            setCoverPreviewUrl('');
                            if (coverInputRef.current) coverInputRef.current.value = '';
                            if (editing?.id) setRemoveExistingCover(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setCoverFile(file);
                  }}
                />
                <small>{editing?.id ? 'Si tu ne changes pas, on garde l’image actuelle.' : 'PNG/JPG/WebP conseillés.'}</small>
              </div>
              <div className="field">
                <label>Galerie (multi)</label>
                <div
                  className={`dropzone ${galleryDragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setGalleryDragOver(true); }}
                  onDragLeave={() => setGalleryDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setGalleryDragOver(false);
                    const files = e.dataTransfer?.files;
                    if (files && files.length) setGalleryFiles(files);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      galleryInputRef.current?.click();
                    }
                  }}
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <div className="dropzone-left">
                    <div className="dropzone-title">Glisser-déposer des images</div>
                    <div className="dropzone-sub">ou cliquer pour sélectionner (multi)</div>
                  </div>
                  <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>
                    {galleryPreviewUrls.length ? `${galleryPreviewUrls.length} fichier(s)` : (Array.isArray(editing?.gallery) ? `${editing.gallery.length} image(s)` : '0')}
                  </div>
                </div>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length) setGalleryFiles(files);
                  }}
                />

                {galleryPreviewUrls.length > 0 ? (
                  <div className="thumbs">
                    {galleryPreviewUrls.map((u, idx) => (
                      <div
                        key={u + idx}
                        className="thumb-wrap draggable"
                        draggable
                        onDragStart={() => { galleryDragRef.current = { type: 'new', index: idx }; }}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = galleryDragRef.current;
                          if (from?.type !== 'new') return;
                          if (typeof from.index !== 'number') return;
                          reorderNewGallery(from.index, idx);
                          galleryDragRef.current = { type: '', index: -1 };
                        }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      >
                        <img className="thumb" src={u} alt={`Aperçu ${idx + 1}`} />
                        <div className="thumb-actions">
                          <button
                            type="button"
                            title="Supprimer l’image"
                            aria-label="Supprimer l’image"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeSelectedGalleryAt(idx);
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (editing?.id && Array.isArray(editing?.gallery) && editing.gallery.length > 0) ? (
                  <div className="thumbs">
                    {existingGalleryOrder.map((name, idx) => {
                      const removed = removedExistingGallery.includes(String(name));
                      return (
                        <div
                          key={String(name) + idx}
                          className={`thumb-wrap draggable ${removed ? 'removed' : ''}`}
                          draggable
                          onDragStart={() => { galleryDragRef.current = { type: 'existing', index: idx }; }}
                          onDragOver={(e) => { e.preventDefault(); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = galleryDragRef.current;
                            if (from?.type !== 'existing') return;
                            if (typeof from.index !== 'number') return;
                            reorderExistingGallery(from.index, idx);
                            galleryDragRef.current = { type: '', index: -1 };
                          }}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        >
                          <img
                            className="thumb"
                            src={pb.files.getURL(editing, name, { thumb: '240x0' })}
                            alt={`Galerie ${idx + 1}`}
                          />
                          <div className="thumb-actions">
                            <button
                              type="button"
                              title={removed ? 'Annuler la suppression' : 'Supprimer l’image'}
                              aria-label={removed ? 'Annuler la suppression' : 'Supprimer l’image'}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRemoveExistingGallery(name);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <small>{editing?.id ? 'Si tu ne changes pas, on conserve la galerie actuelle.' : 'Tu peux déposer plusieurs images.'}</small>
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteRealisationsPage;
